import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PlansDownloadCta from '../components/PlansDownloadCta';
import PlaneCard from '../components/PlaneCard';
import CityStatsSkeleton from '../components/skeletons/CityStatsSkeleton';
import { PlaneCardSkeletonGrid } from '../components/skeletons/PlaneCardSkeleton';
import { useSeoMeta, truncateDescription } from '../hooks/useSeoMeta';
import { useJsonLd } from '../hooks/useJsonLd';
import { cityPlanesPageJsonLd } from '../seo/jsonLd';
import { getCityPlanes } from '../api/client';
import { getCityPlanesSeoContent } from '../data/cityPlanesSeo';
import { cityDisplayNameFromSlug } from '../data/featuredCityPreviews';
import { OG_IMAGE_URL } from '../pages/home-redesign/cityLinks';
import type { CitySeoApi, PlanePublic } from '../api/types';

const cardBorder = 'border-[rgba(74,33,71,0.1)]';

const CityPlanes: React.FC = () => {
  const { city } = useParams<{ city: string }>();
  const [planes, setPlanes] = useState<PlanePublic[]>([]);
  const [cityChicas, setCityChicas] = useState<number>(0);
  const [cityTotalPlanes, setCityTotalPlanes] = useState<number>(0);
  const [cityPlanesSeo, setCityPlanesSeo] = useState<CitySeoApi>({
    title: 'Planes entre mujeres en tu ciudad',
    paragraphs: [
      'Descubre planes en tu ciudad y conecta con mujeres como tu.',
      `Filtra por dia, encuentra un plan y unete desde la app de Chisme.`,
    ],
  });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string>('all');
  const [seoExpanded, setSeoExpanded] = useState(false);

  const cityDisplayName = cityDisplayNameFromSlug(city);

  const metaDescriptionRaw = `${cityPlanesSeo.paragraphs[0]} ${cityPlanesSeo.paragraphs[1]}`;
  const metaDescription = truncateDescription(metaDescriptionRaw);

  useSeoMeta({
    title: `Planes en ${cityDisplayName} | Chisme`,
    description: metaDescription,
    url: `https://chisme.social/ciudades/${city}`,
    image: OG_IMAGE_URL,
    locale: 'es',
  });

  const jsonLd = useMemo(
    () => (city && cityDisplayName ? cityPlanesPageJsonLd(cityDisplayName, city) : null),
    [city, cityDisplayName],
  );
  useJsonLd(jsonLd);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getCityPlanes(city ?? '')
      .then((payload) => {
        if (!active) return;
        setPlanes(payload.planes);
        setCityChicas(payload.city.chicas);
        setCityTotalPlanes(payload.city.totalPlanes);
        setCityPlanesSeo(payload.seo);
      })
      .catch((error) => {
        console.error('Failed to load city planes:', error);
        if (!active) return;
        setPlanes([]);
        setCityChicas(0);
        setCityTotalPlanes(0);
        setCityPlanesSeo(getCityPlanesSeoContent(city, cityDisplayNameFromSlug(city)));
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [city]);

  const getDayCategory = (dateString: string): string => {
    const planDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    planDate.setHours(0, 0, 0, 0);

    if (planDate.getTime() === today.getTime()) {
      return 'today';
    } else if (planDate.getTime() === tomorrow.getTime()) {
      return 'tomorrow';
    } else {
      return planDate.toLocaleDateString('es-ES', { weekday: 'long' }).toLowerCase();
    }
  };

  const getDayTabs = () => {
    const today = new Date();
    const tabs = [
      { id: 'all', label: 'Todos' },
      { id: 'today', label: 'Hoy' },
      { id: 'tomorrow', label: 'Mañana' },
    ];

    for (let i = 2; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const dayName = date.toLocaleDateString('es-ES', { weekday: 'long' });
      const dayId = dayName.toLowerCase();
      tabs.push({ id: dayId, label: dayName.charAt(0).toUpperCase() + dayName.slice(1) });
    }

    return tabs;
  };

  const filteredPlanes =
    selectedDay === 'all'
      ? planes
      : planes.filter((plane) => getDayCategory(plane.date) === selectedDay);

  const dayTabs = getDayTabs();

  const planesHoyCount = planes.filter((p) => getDayCategory(p.date) === 'today').length;

  return (
      <div className="container-chisme py-10 lg:py-16">
        <div className="mb-10">
          <Link
            to="/ciudades"
            className={`inline-flex items-center rounded-full border ${cardBorder} bg-white px-5 py-2.5 font-jakarta text-sm font-semibold text-[#2a0f2a] transition-colors hover:border-chisme-primary/30 hover:text-chisme-primary`}
          >
            ← Todas las ciudades
          </Link>
        </div>

        <header className="mb-10 text-center">
          <h1 className="font-instrument text-4xl leading-tight tracking-[-0.02em] text-[#2a0f2a] sm:text-5xl md:text-6xl lg:text-[64px] lg:leading-[0.98]">
            <span className="block">Planes en</span>
            <span className="block italic text-chisme-primary">{cityDisplayName}</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl font-jakarta text-lg leading-relaxed text-[#5a3158] md:text-xl">
            Descubre y únete a planes cerca de ti
          </p>
        </header>

        {loading ? (
          <CityStatsSkeleton />
        ) : (
          <div className="mx-auto mb-10 max-w-xl">
            <div className={`grid grid-cols-3 gap-0 overflow-hidden rounded-2xl border ${cardBorder} bg-chisme-bg`}>
              <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3.5 text-center sm:px-4 sm:py-4">
                <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                  Chicas
                </p>
                <p className="mt-1 font-jakarta text-lg font-bold tabular-nums leading-none text-[#2a0f2a] sm:text-xl">
                  {cityChicas.toLocaleString('es-ES')}
                </p>
              </div>
              <div className="border-r border-[rgba(74,33,71,0.1)] px-3 py-3.5 text-center sm:px-4 sm:py-4">
                <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                  Planes
                </p>
                <p className="mt-1 font-jakarta text-lg font-bold tabular-nums text-[#2a0f2a] sm:text-xl">
                  {cityTotalPlanes || planes.length}
                </p>
              </div>
              <div className="px-3 py-3.5 text-center sm:px-4 sm:py-4">
                <p className="font-jetbrains text-[10px] font-medium uppercase leading-tight tracking-wide text-[#5a3158] sm:text-xs">
                  Hoy
                </p>
                <p className="mt-1 font-jakarta text-lg font-bold tabular-nums text-chisme-primary sm:text-xl">
                  {planesHoyCount}
                </p>
              </div>
            </div>
          </div>
        )}

        <div
          className={`-mx-1 mb-10 flex justify-center gap-2 overflow-x-auto px-1 pb-2 scrollbar-hide ${
            loading ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          {dayTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedDay(tab.id)}
              className={`whitespace-nowrap rounded-full px-6 py-3 font-jakarta text-sm font-semibold transition-all ${
                selectedDay === tab.id
                  ? 'bg-chisme-primary text-white'
                  : `border ${cardBorder} bg-white text-[#2a0f2a] hover:border-chisme-primary/30`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <PlaneCardSkeletonGrid count={6} />
        ) : (
          <>
            {filteredPlanes.length === 0 ? (
              <div className={`mx-auto max-w-lg rounded-3xl border ${cardBorder} bg-white px-6 py-16 text-center`}>
                <p className="font-jakarta text-xl text-[#2a0f2a] mb-3">Hay más planes esperándote en la app</p>
                <p className="font-jakarta text-sm text-[#5a3158]">
                  Descarga Chisme para ver todo lo que pasa cerca de ti, o lanza tu propio plan.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
                {filteredPlanes.map((plane, index) => (
                  <PlaneCard
                    key={plane.slug}
                    plane={plane}
                    imagePriority={index < 3 ? 'high' : 'low'}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <PlansDownloadCta
          title="¿Quieres crear tu propio plan?"
          body="Descarga la app y empieza a conectar con mujeres como tú"
        />

        <section
          className="mx-auto mt-12 max-w-3xl"
          aria-labelledby="seo-planes-ciudad-heading"
        >
          <h2
            id="seo-planes-ciudad-heading"
            className="font-jakarta text-lg font-bold text-[#2a0f2a] md:text-xl"
          >
            {cityPlanesSeo.title}
          </h2>
          <div
            className={`mt-3 font-jakarta text-sm leading-relaxed text-[#5a3158] md:text-base ${
              seoExpanded ? '' : 'line-clamp-3'
            }`}
          >
            <p>{cityPlanesSeo.paragraphs[0]}</p>
            <p className="mt-3">{cityPlanesSeo.paragraphs[1]}</p>
          </div>
          <button
            type="button"
            onClick={() => setSeoExpanded((v) => !v)}
            className="mt-3 font-jakarta text-sm font-semibold text-chisme-primary hover:underline underline-offset-2"
            aria-expanded={seoExpanded}
          >
            {seoExpanded ? 'Ver menos' : 'Ver más'}
          </button>
        </section>
      </div>
  );
};

export default CityPlanes;
